import type { MatchingResponse } from "@/lib/api/kundali";

const EXPECTED_KOOTAS = new Map([
  ["Varna", 1],
  ["Vashya", 2],
  ["Tara", 3],
  ["Yoni", 4],
  ["Graha Maitri", 5],
  ["Gana", 6],
  ["Bhakoot", 7],
  ["Nadi", 8],
]);

export interface MatchingResultQuality {
  complete: boolean;
  reasons: string[];
}

export function inspectMatchingResult(result: MatchingResponse): MatchingResultQuality {
  const reasons = [...scoreReasons(result), ...kootaReasons(result), ...guidanceReasons(result)];
  return { complete: reasons.length === 0, reasons };
}

function scoreReasons(result: MatchingResponse): string[] {
  return [
    isBoundedNumber(result.total_score, 0, 36) ? null : "invalid_total_score",
    result.max_score === 36 ? null : "invalid_max_score",
    isBoundedNumber(result.percentage, 0, 100) ? null : "invalid_percentage",
  ].filter((reason): reason is string => reason !== null);
}

function kootaReasons(result: MatchingResponse): string[] {
  const byName = new Map((result.kootas ?? []).map((koota) => [koota.name, koota]));
  const itemReasons = [...EXPECTED_KOOTAS].flatMap(([name, max]) =>
    inspectKoota(byName.get(name), name, max)
  );
  return [
    ...itemReasons,
    ...(byName.size === EXPECTED_KOOTAS.size ? [] : ["unexpected_koota_count"]),
  ];
}

function inspectKoota(
  koota: MatchingResponse["kootas"][number] | undefined,
  name: string,
  max: number
): string[] {
  const slug = name.toLowerCase().replace(/\s+/g, "_");
  if (!koota) return [`missing_koota_${slug}`];
  return [
    koota.max === max && isBoundedNumber(koota.score, 0, max) ? null : `invalid_koota_${slug}`,
    koota.description?.trim() ? null : `missing_explanation_${slug}`,
  ].filter((reason): reason is string => reason !== null);
}

function guidanceReasons(result: MatchingResponse): string[] {
  return [
    result.recommendation?.trim() ? null : "missing_practical_recommendation",
    result.person_a_details && result.person_b_details ? null : "missing_person_details",
    result.mangal_dosha ? null : "missing_mangal_dosha",
  ].filter((reason): reason is string => reason !== null);
}

function isBoundedNumber(value: number, min: number, max: number): boolean {
  return Number.isFinite(value) && value >= min && value <= max;
}
